import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';

const IdealWeightCalculator = () => {
  const [gender, setGender] = useState('male');
  const [height, setHeight] = useState(170);

  const formulas = useMemo(() => {
    const inches = height / 2.54;
    const over5ft = Math.max(inches - 60, 0);
    const isMale = gender === 'male';
    return {
      robinson: isMale ? 52 + 1.9 * over5ft : 49 + 1.7 * over5ft,
      miller: isMale ? 56.2 + 1.41 * over5ft : 53.1 + 1.36 * over5ft,
      devine: isMale ? 50 + 2.3 * over5ft : 45.5 + 2.3 * over5ft,
      hamwi: isMale ? 48 + 2.7 * over5ft : 45.5 + 2.2 * over5ft,
      bmiLow: 18.5 * Math.pow(height / 100, 2),
      bmiHigh: 24.9 * Math.pow(height / 100, 2),
    };
  }, [gender, height]);

  const avg = (formulas.robinson + formulas.miller + formulas.devine + formulas.hamwi) / 4;

  return (
    <CalculatorLayout
      title="Ideal Weight Calculator"
      subtitle="Estimate ideal body weight using multiple medically-recognized formulas."
      category="Fitness"
      description={<>
        <p>Several formulas exist to estimate ideal body weight; we show four common ones (Robinson, Miller, Devine, Hamwi) plus the BMI-based healthy weight range. None is perfect — ideal weight depends on age, body frame, muscle mass, and individual factors.</p>
        <p>Use this as a general reference and consult a healthcare provider for personalized guidance.</p>
      </>}
      formulaNote="Devine: Men = 50 + 2.3 × (height_in − 60) kg ; Women = 45.5 + 2.3 × (height_in − 60) kg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Gender</label>
            <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="male" /> Male</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="female" /> Female</label>
            </RadioGroup>
          </div>
          <SliderInput label="Height" unit="cm" value={height} onChange={setHeight} min={120} max={220} step={1} />
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Average Ideal Weight</div>
          <div className="font-display text-4xl font-bold text-bharat-navy display-font mt-1">{avg.toFixed(1)} <span className="text-base text-slate-500">kg</span></div>
          <div className="text-xs text-slate-500 mt-1">Based on 4 formulas</div>

          <div className="mt-5 space-y-2">
            {[
              { label: 'Robinson (1983)', value: formulas.robinson },
              { label: 'Miller (1983)', value: formulas.miller },
              { label: 'Devine (1974)', value: formulas.devine },
              { label: 'Hamwi (1964)', value: formulas.hamwi },
            ].map(f => (
              <div key={f.label} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-600">{f.label}</span>
                <span className="display-font font-semibold text-bharat-navy">{f.value.toFixed(1)} kg</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-white border border-indgreen-200 rounded-lg">
            <div className="text-[10px] uppercase tracking-wider text-indgreen-700 font-semibold">Healthy BMI Range</div>
            <div className="display-font font-semibold text-indgreen-700 mt-1">{formulas.bmiLow.toFixed(1)} – {formulas.bmiHigh.toFixed(1)} kg</div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default IdealWeightCalculator;
